import { z } from 'zod';

const optStr = (max = 200) => z.preprocess(
    (v) => (v === null || v === undefined ? undefined : v),
    z.string().max(max).optional().or(z.literal(''))
);

/* ── Chi tiết đơn hàng (dòng sản phẩm) ── */
export const orderDetailSchema = z.object({
    id: optStr(100),
    productId: optStr(100),
    productCode: optStr(50),
    productName: z.string().min(1, 'Tên SP không được trống'),
    specs: optStr(200),
    process: optStr(1000),
    materialId: optStr(100),
    unit: optStr(20),
    quantity: z.coerce.number().int().min(1, 'SL >= 1'),
    unitPrice: z.coerce.number().min(0, 'Đơn giá >= 0'),
    purchasePrice: z.coerce.number().min(0).optional(),
    priceVND: z.coerce.number().optional(),
    priceUSD: z.coerce.number().optional(),
    amount: z.coerce.number().min(0).optional(),
    costPrice: z.coerce.number().min(0).optional(),
    profitMargin: z.coerce.number().min(0).max(100).optional(),
    allowancePercent: z.coerce.number().min(0).max(100).optional(),
    stockQty: z.coerce.number().min(0).optional(),
    lsxQty: z.coerce.number().min(0).optional(),
    notes: optStr(500),
    poReference: z.string().max(50, 'Tối đa 50 ký tự').refine(val => !val.includes(',') && !val.includes('\\n'), { message: 'Không được chứa dấu phẩy hoặc xuống dòng' }).optional().or(z.literal('')),
    soReference: optStr(50),
    deliveryDeadline: optStr(),
    customerPo: optStr(50),
    pnNo: optStr(50),
    pjNo: optStr(50),
    status: optStr(50),
    prepressCode: optStr(100),
    prepressPaperSize: optStr(100),
    prepressUps: z.coerce.number().optional(),
    prepressPrintSheets: z.coerce.number().optional(),
    prepressNotes: optStr(500),
    prepressCount: z.coerce.number().min(0).optional(),
    prepressPrepressCode: optStr(100),
    quotationDetailId: optStr(100),
    remainingPlanQty: z.coerce.number().optional(),
});

export type OrderDetailFormData = z.infer<typeof orderDetailSchema>;

/* ── Form chính ── */
export const orderFormSchema = z.object({
    // — Thông tin đơn hàng —
    orderId: optStr(30),
    // removed poNumber and soNumber
    customerId: z.string().min(1, 'Vui lòng chọn khách hàng'),
    customerName: optStr(),
    contactPerson: optStr(100),
    phone: optStr(20),
    email: z.string().email('Email không hợp lệ').optional().or(z.literal('')),
    updateCustomerInfo: z.boolean().optional(),

    // — Ngày tháng —
    orderDate: z.string().min(1, 'Ngày đặt hàng không được để trống'),
    deliveryDeadline: optStr(),
    approvedDate: optStr(),

    // — Phân loại —
    category: z.enum(['MANUFACTURING', 'OUTSOURCE', 'COMMERCIAL']).optional().default('MANUFACTURING'),
    priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']).optional().default('NORMAL'),
    orderType: optStr(),
    sampleType: optStr(),

    // — Chi tiết sản phẩm (N dòng) —
    orderDetails: z.array(orderDetailSchema)
        .min(1, 'Phải có ít nhất 1 sản phẩm')
        .refine(
            (items) => items.some(item => (item.productId && item.productId.trim() !== '') || (item.productName && item.productName.trim() !== '')),
            { message: 'Đơn hàng phải có ít nhất 1 sản phẩm đã chọn' }
        ),

    // — Tài chính (tổng hợp) —
    vatRate: z.coerce.number().min(0).max(100),
    shippingFee: z.coerce.number().min(0),
    currency: z.enum(['VND', 'USD']),
    exchangeRate: z.coerce.number().min(0),
    deposit: optStr(200),
    paymentTerm: optStr(200),

    // — Giao hàng —
    deliveryAddress: optStr(500),
    deliveryMethod: optStr(100),
    deliveryNotes: optStr(500),

    // — Trạng thái —
    status: z.enum(['DRAFT', 'PENDING', 'CONFIRMED', 'APPROVED', 'PLANNED', 'IN_PROGRESS', 'IN_PRODUCTION', 'COMPLETED', 'DELIVERED', 'INVOICED', 'CLOSED', 'CANCELLED']),
    notes: optStr(1000),

    // — NV Kinh doanh —
    salesUserId: optStr(100),
}).superRefine((val, ctx) => {
    if (val.deliveryDeadline && val.orderDate && val.deliveryDeadline < val.orderDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['deliveryDeadline'],
            message: 'Hạn giao hàng không được nhỏ hơn ngày đặt hàng',
        });
    }
});

export type OrderFormData = z.infer<typeof orderFormSchema>;

export interface OrderDetail {
    id?: string;
    detailId?: string;
    productId?: string | null;
    productCode?: string | null;
    productName: string;
    productGroup?: string | null;
    stockType?: string | null;
    quantity: number;
    unit?: string | null;
    specs?: string | null;
    process?: string | null;
    materialId?: string | null;
    materialName?: string | null;
    unitPrice: number;
    amount: number;
    priceVND?: number | null;
    priceUSD?: number | null;
    totalVND?: number | null;
    totalUSD?: number | null;
    deliveryDate?: string | null;
    stockQty?: number | null;
    lsxId?: string | null;
    lsxQty?: number | null;
    allowancePercent?: number | null;
    notes?: string | null;
    poReference?: string | null;
    soReference?: string | null;
    customerPo?: string | null;
    pnNo?: string | null;
    pjNo?: string | null;
    completedQty?: number | null;
    status?: string | null;
    prepressPaperSize?: string | null;
    prepressCount?: number | null;
    prepressPrepressCode?: string | null;
}

export interface Order {
    id: string;
    orderId: string;
    // removed poNumber and soNumber
    customerId: string;
    customerName?: string | null;
    contactPerson?: string | null;
    phone?: string | null;
    email?: string | null;
    vatRate: number;
    vatAmount: number;
    shippingFee: number;
    amount?: number;
    totalAmount: number;
    currency: string;
    exchangeRate: number;
    deposit?: string | null;
    paymentTerm?: string | null;
    orderDate: string;
    deliveryDeadline?: string | null;
    deliveryAddress?: string | null;
    deliveryMethod?: string | null;
    deliveryNotes?: string | null;
    salesUserId?: string | null;
    quotationId?: string | null;
    approvedDate?: string | null;
    status: 'DRAFT' | 'PENDING' | 'CONFIRMED' | 'IN_PRODUCTION' | 'COMPLETED' | 'DELIVERED' | 'INVOICED' | 'CLOSED' | 'CANCELLED';
    version: number;
    notes?: string | null;
    customer?: { id: string; name: string; code?: string; shortName?: string; address?: string } | null;
    salesUser?: { id: string; fullName: string } | null;
    quotation?: { id: string; quotationId: string } | null;
    orderDetails?: OrderDetail[];
    createdAt: string;
    updatedAt: string;
}
